import { getNextElement, isFocusable } from './helpers.js'

/**
 * Handle keyboard focus inside of a swiper
 *
 * - Automatically moves to the slide where a focus occurs
 * - Enables the 'Escape' key to focus the swiper container
 *   (especially important for looping swipers which can trap keyboard focus)
 *
 * @param {import('swiper').Swiper} swiper
 * @returns {() => void} Cleanup function (automatically called when Swiper is destroyed)
 */
export default function (swiper) {
  let swiperElement = swiper.el

  function onBlur() {
    swiperElement.removeAttribute('tabindex')
  }
  swiperElement.addEventListener('blur', onBlur)

  /**
   * @param {KeyboardEvent} event
   */
  function onKeydown(event) {
    if (event.key === 'Escape') {
      swiperElement.setAttribute('tabindex', '-1')
      swiperElement.focus()
    }

    if (event.key === 'Tab' && !event.shiftKey && event.target === swiperElement) {
      event.preventDefault()
      getNextElement(swiperElement, isFocusable)?.focus()
    }
  }
  swiperElement.addEventListener('keydown', onKeydown)

  /**
   * @param {FocusEvent} event
   */
  function onFocusin(event) {
    for (let slide of swiperElement.querySelectorAll('.swiper-slide')) {
      if (slide.contains(event.target)) {
        let index =
          'swiperSlideIndex' in slide.dataset
            ? Number(slide.dataset.swiperSlideIndex)
            : [...slide.parentNode.children].indexOf(slide)
        swiper.slideTo(index)

        if (index === 1) {
          swiperElement.focus()
        }
        break
      }
    }
  }
  let swiperWrapper = swiperElement.querySelector('.swiper-wrapper')
  swiperWrapper.addEventListener('focusin', onFocusin)

  function cleanup() {
    swiperElement.removeEventListener('blur', onBlur)
    swiperElement.removeEventListener('keydown', onKeydown)
    swiperWrapper.removeEventListener('focusin', onFocusin)
  }

  swiper.once('beforeDestroy', cleanup)

  return cleanup
}
